/* eslint-disable @typescript-eslint/member-ordering */
import {
    DataLabel,
    DataLabels,
} from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/properties/data-label';
import { Trendline } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/properties/trendline';
import {
    ChartShapeInfoConverter,
} from '@src/slide/domain/ui-model/converter/node-property/module-property/chart/common/chart-shape-info-converter';
import _has from 'lodash/has';
import type { DataPoint, IChartMarker, IChartSeries } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/sub-chart-properties.interface';
import type { ICTDPt, IAreaChartSerData, IStockChartSerData,
    IBarChartSerData, IBubbleChartSerData, IValuePointData,
    ILineChartSerData, IScatterChartSerData, IRadarChartSerData,
    ISubChartSerData, ICTNumRef, ICTStrRef, DeltaOf,
    ICategory } from '@tencent/slide-sdk-model';
import type { IChartBaseShapeProps } from '@src/slide/domain/ui-model/converter/node-property/module-property/chart/common/chart-shape-info-converter';
import type { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { ISeriesProperty } from '../../../property/chart/sub-chart/series/series.interface';
import { IChartShapeProps } from '../../../Interface/chart-v3/chart-properties.interface';

export type NumberRef = ICTNumRef;
export type StringRef = ICTStrRef;

export interface SerRules {
    count: number;
    categoryPointList: Array<IValuePointData | undefined>;
}

export interface SdkChartSeries {
    idx: number;
    order: number;
    categoryAxisData: StringRef | NumberRef;
    values: NumberRef;
    seriesText: StringRef;
    // 暂时不管这些类型，目前改动的只有categoryAxisData, values和seriesText，其余的类型待完善
    dataLabels?: any;
    props: any;
    [key: string]: any;
}

// 1. category、value有空数据点, pt数量小于count，通过idx定位对应的索引，其余用undefined补全
// 2. category、value的长度不一致，通过"较长"的进行补全，dataList长度以较长的为准
// 3. category没有count和pt字段。这个需要和2区分，2是有pt字段，但是长度不同。这个是根本么有pt字段。虽然最终都是补全，但是需要区分下二者
export interface DataListItem {
    idx?: number;
    categoryPoint?: {
        // 散点、气泡图是number
        value?: string | number;
        formatCode?: string;
    };
    valuePoint?: {
        value?: string | number;
        formatCode?: string;
    }

    dataPoint?: DataPoint;
    dataLabel?: DataLabel;

    id: string; // dataList中数据的唯一标识，可以通过id直接关联到某个dataListItem
}

/**
 * 带有marker的系列数据
 */
export type WithMarkerSubChartSerData =
| ILineChartSerData
| IRadarChartSerData
| IScatterChartSerData;


export type WithTrendlineSubChartSerData =
| IAreaChartSerData
| ILineChartSerData
| IStockChartSerData
| IScatterChartSerData
| IBarChartSerData
| IBubbleChartSerData;
export class ChartSeries implements IChartSeries {
    public idx?: number;
    public name: string;
    public order?: number;
    public seriesId: string;    // series的唯一标识，能够通过seriesId找到指定series

    public categoryFormatCode?: string;
    public valueFormatCode?: string;
    public dataList: DataListItem[] = [];
    public dataPoints?: Array<DataPoint>;
    public dataLabels?: DataLabels;
    // 额外剩下的dataPoint，比如：子母图含有"其他"项，存在大于count数的data point
    public otherDPt?: DataPoint;
    public otherDLbl?: DataLabel;

    public props: IChartShapeProps = { style: {} };
    public trendlines?: Trendline[];
    // 很多种图形都有marker，这里加到通用的series里处理
    public marker?: IChartMarker;

    public disable?: boolean;
    public categoryType?: 'sr' | 'nr' | 'sl' | 'nl';
    public valueType?: 'sr' | 'nr' | 'sl' | 'nl';
    public values?: any;
    public isEmptySeriesName: boolean;
    public valueCount?: number;
    protected property: ISeriesProperty;


    public constructor(series: ISubChartSerData, options: SerRules) {
        const { idx, order, valueCount } = series;
        // 生成不同长度的series空字符串，使 视觉上是空，但是又能被g2的视觉通道分类
        this.idx = idx?.val;
        this.order = order?.val;
        // 这个不会直接用于series，是用于上层结合全部series.value来计算最终展示的count，并通过options传入
        this.valueCount = valueCount;

        this.setSeriesName(series);
        this.setSeriesId();
        this.setDataList(series, options);
        this.setDataFormatCodeAndType(series);
        this.setDataLabels(series);
        this.setTrendline(series);
        this.setMarker(series);

        // 现在的sdk数据和之前的不一样，现在xsd中并不是所有的interface都有这些属性，但是之前是统一处理的
        // 这里加一个in来断言key，防止ts报错，逻辑上这么做没问题
        if (series.otherDPt) {
            this.otherDPt = {};
            ChartShapeInfoConverter.convertPartialDataFromSdkToUi(series.otherDPt, this.otherDPt as IShapeModel);
        }
        if (series.otherDLbl) {
            this.otherDLbl = new DataLabel(series.otherDLbl, this.seriesId);
        }

        // shape、文本样式
        ChartShapeInfoConverter.convertPartialDataFromSdkToUi(
            series as unknown as IChartBaseShapeProps,
            this as unknown as IShapeModel,
        );
    }

    public static migrateFromV2(chartSeriesV2): IChartSeries {
        // 这里用一个v3的空model作为壳子，继承chartSeriesV2的数据
        const chartSeries = new this(({} as any), { count: 0, categoryPointList: [] });

        Object.assign(chartSeries, chartSeriesV2);
        // @ts-ignore
        return chartSeries;
    }

    // eslint-disable-next-line complexity
    public static convertDataUiToSdk(uiData) {
        if (!uiData) return uiData;
        const sdkSeries: DeltaOf<ISubChartSerData> = {};

        if (_has(uiData, 'idx')) sdkSeries.idx = { val: uiData.idx };
        if (_has(uiData, 'order')) sdkSeries.order = { val: uiData.order };
        if (_has(uiData, 'isEmptySeriesName') || _has(uiData, 'name')) {
            sdkSeries.tx = { strRef: this.convertToSdkStringRef([uiData.isEmptySeriesName ? '' : uiData.name]) };
        }
        if (_has(uiData, 'dataList')) {
            // @ts-ignore
            sdkSeries.categories = this.convertDataListToCategories(uiData.dataList);
        }
        if (_has(uiData, 'valueCount')) sdkSeries.valueCount = uiData.valueCount;

        // 气泡图，散点图可能要重写这里
        if (_has(uiData, 'categoryType')) {
            sdkSeries.categoryAxisData = sdkSeries.categoryAxisData ?? {};
            sdkSeries.categoryAxisData.type = uiData.categoryType || 'sr';
        }
        if (_has(uiData, 'categoryFormatCode')) {
            sdkSeries.categoryAxisData = sdkSeries.categoryAxisData ?? {};
            sdkSeries.categoryAxisData.formatCode = uiData.categoryFormatCode;
        }
        if (_has(uiData, 'valueType')) {
            sdkSeries.values = sdkSeries.values ?? {};
            sdkSeries.values.type = uiData.valueType || 'nr';
        }
        if (_has(uiData, 'valueFormatCode')) {
            sdkSeries.values = sdkSeries.values ?? {};
            sdkSeries.values.formatCode = uiData.valueFormatCode;
        }

        if (_has(uiData, 'dataLabels')) {
            if (!uiData.dataLabels) {
                sdkSeries.dLbls = uiData.dataLabels;
            } else {
                sdkSeries.dLbls = DataLabels.convertDataUiToSdk(uiData.dataLabels);
            }
        }

        if (_has(uiData, 'trendlines')) {
            const sdkSer = sdkSeries as WithTrendlineSubChartSerData;
            if (!uiData.trendlines) {
                // @ts-ignore
                sdkSer.trendline = null;
            } else {
                sdkSer.trendline = uiData.trendlines.map(trendline => (trendline
                    ? Trendline.convertDataUiToSdk(trendline) : trendline));
            }
        }

        if (_has(uiData, 'marker')) {
            const sdkSer = sdkSeries as WithMarkerSubChartSerData;
            if (uiData.marker) {
                sdkSer.marker = this.convertMarkerDataUiToSdk(uiData.marker);
            } else {
                sdkSer.marker = uiData.marker;
            }
        }

        if (_has(uiData, 'otherDPt')) {
            if (uiData.otherDPt) {
                sdkSeries.otherDPt = sdkSeries.otherDPt ?? {};
                ChartShapeInfoConverter.convertPartialDataFromUiToSdk(uiData.otherDPt, sdkSeries.otherDPt);
            } else {
                sdkSeries.otherDPt = uiData.otherDPt;
            }
        }

        if (_has(uiData, 'otherDLbl')) {
            if (uiData.otherDLbl) {
                sdkSeries.otherDLbl = DataLabel.convertDataUiToSdk(uiData.otherDLbl);
            } else {
                sdkSeries.otherDLbl = uiData.otherDLbl; // 字段数据为空也要赋值。可能是删除字段
            }
        }

        ChartShapeInfoConverter.convertPartialDataFromUiToSdk(uiData, sdkSeries);

        return sdkSeries;
    }

    public static convertUIPointToSdkPoint(uiPoint, shouldBeNum = false): ICategory['categoryPoint'] | ICategory['valuePoint'] {
        if (uiPoint.value !== undefined) {
            uiPoint.value = String(uiPoint.value);
            if (shouldBeNum && isNaN(Number(uiPoint.value))) {
                uiPoint.value = '0';
            }
        }

        return {
            ...uiPoint,
            v: uiPoint.value,
        };
    }

    public static convertToSdkStringRef(uiData?: Array<string|number>): StringRef {
        return {
            strCache: {
                ptCount: { val: uiData?.length },
                pt: uiData?.map((value, idx)  => ({
                    idx,
                    v: (value || value === 0) ? String(value) : value as string,
                })).filter(point => !!point.v),
            },
        };
    }

    public static convertMarkerDataUiToSdk(marker) {
        if (!marker) return marker;

        const sdkData: any = { props: {} };

        if (marker.size !== undefined) {
            if (!marker.size) {
                sdkData.size = marker.size;
            } else {
                sdkData.size = { val: marker.size };
            }
        }

        if (marker.symbol !== undefined) {
            if (!marker.symbol) {
                sdkData.symbol = marker.symbol;
            } else {
                sdkData.symbol = { val: marker.symbol };
            }
        }

        ChartShapeInfoConverter.convertPartialDataFromUiToSdk(marker, sdkData);

        return sdkData;
    }

    public static convertDataPointUiToSdk(uiDataPoint) {
        const sdkDataPoint: any = {};

        ChartShapeInfoConverter.convertPartialDataFromUiToSdk(uiDataPoint, sdkDataPoint);

        if (uiDataPoint.marker) {
            sdkDataPoint.marker = {
                size: uiDataPoint.marker.size,
                symbol: uiDataPoint.marker.symbol,
                props: {},
            };

            ChartShapeInfoConverter.convertPartialDataFromUiToSdk(uiDataPoint.marker, sdkDataPoint.marker);
        }

        return sdkDataPoint;
    }

    public static convertDataListToCategories(uiDataList: DataListItem[]):
        (DeltaOf<ICategory> | undefined)[] | undefined {
        if (!uiDataList || !Array.isArray(uiDataList)) return uiDataList;

        return uiDataList.map((uiDataItem) => {
            if (typeof uiDataItem === 'undefined') return undefined;
            const sdkCategoryItem: DeltaOf<ICategory> = {};

            // undefined的值是前端补的，原数据里没有undefined的值，所以在不为undefined的时候才去转换
            if (uiDataItem?.categoryPoint?.value !== undefined) {
                sdkCategoryItem.categoryPoint = this.convertUIPointToSdkPoint(uiDataItem.categoryPoint);
            }
            // office里空值会被忽略, 不会创建valuePoint, office里空值会被忽略, 不会创建valuePoint, ooxml里不会创建<c:pt idx="n">...</c:pt>
            if ((uiDataItem?.valuePoint?.value as unknown) === '') {
                sdkCategoryItem.valuePoint = undefined;
            } else if (uiDataItem?.valuePoint?.value !== undefined) {
                sdkCategoryItem.valuePoint = this.convertUIPointToSdkPoint(uiDataItem.valuePoint, true);
            }

            if (_has(uiDataItem, 'dataLabel')) {
                if (uiDataItem.dataLabel) {
                    sdkCategoryItem.dataLabel = DataLabel.convertDataUiToSdk(uiDataItem.dataLabel);
                } else {
                    sdkCategoryItem.dataLabel = uiDataItem.dataLabel; // 字段数据为空也要赋值。可能是删除字段
                }
            }

            if (_has(uiDataItem, 'dataPoint')) {
                if (uiDataItem.dataPoint) {
                    sdkCategoryItem.dataPoint = this.convertDataPointUiToSdk(uiDataItem.dataPoint);
                } else {
                    sdkCategoryItem.dataPoint = uiDataItem.dataPoint; // 字段数据为空也要赋值。可能是删除字段
                }
            }

            return sdkCategoryItem;
        });
    }

    public setSeriesName(series: ISubChartSerData) {
        const { tx } = series;
        const strRefContent = tx?.strRef?.strCache?.pt?.[0]?.v;
        const strValueContent = tx?.v;

        // seriesName是字符串类型，不会有0，直接||就可以
        const seriesName = strRefContent || strValueContent || `${new Array(series.idx?.val).fill(' ')
            .join('')}`;

        this.isEmptySeriesName = !(strRefContent || strValueContent);
        this.name = seriesName;
    }

    public setDataFormatCodeAndType(series: ISubChartSerData) {
        this.categoryFormatCode = series.categoryAxisData?.formatCode;
        this.valueFormatCode = series.values?.formatCode;

        this.categoryType = series.categoryAxisData?.type;
        this.valueType = series.values?.type;
    }

    public setDataList(series: ISubChartSerData, options: SerRules) {
        if (series.disable) {
            this.disable = series.disable;
            this.dataList = [];
            return;
        }

        // 实际有值的数据点
        // 1. 实际展示的图表数据点，根据ser.valueCount来获取（valueCount是 values.count or idx最大值+1）
        // 2. ser.categories，是 ser.values/ser.categoryAxisData的实际值的merge，长度取较大的一个
        // 3. 增加一个变量 disable，来标识 此ser的数据是否展示。disable为false的情况，categories不一定为空
        // 4. 前端ui数据 dataList，实际长度为valueCount为准，来截取ser.categories
        // 5. subChart中每一个ser.dataList的长度相同，长度为ser中values.count 最大的一个。
        // 6. ser.dataList长度不够的，需要通过 ser.categories中的索引，来添加 value为空、有cateogry的空数据点来占位
        // 7. 前端最终的categoryList（坐标轴展示的分类列表），以第一个有ser.categories的数据为准，
        //  后面的数据的category取值，都通过索引，去取这个categoryList，没有对应索引就为空，不管他自己的categories中是不是有对应的category
        if (!options?.count) return;
        this.dataList = new Array(options.count).fill(0)
            .map((_, index): DataListItem => {
                const sdkCategoryItem = series.categories?.[index];
                const id = `dataItem_${index}_${this.seriesId}`;
                const uiDataItem: DataListItem = {
                    id,
                    idx: index,
                };

                // categoryPoint和valuePoint，都有可能是undefined，这里看看是不是补齐
                // 这里补齐为和sdkData同样的数据格式，最后再格式化为valuePoint数据格式
                const categoryPoint = options.categoryPointList[index] || { v: undefined };
                const valuePoint = sdkCategoryItem?.valuePoint || { v: undefined };

                if (sdkCategoryItem.dataPoint !== undefined) {
                    const sdkDataPoint = sdkCategoryItem.dataPoint as ICTDPt;

                    uiDataItem.dataPoint = {};
                    ChartShapeInfoConverter.convertPartialDataFromSdkToUi(
                        sdkDataPoint,
                        uiDataItem.dataPoint as unknown as IShapeModel,
                    );

                    if (sdkDataPoint.marker) {
                        uiDataItem.dataPoint.marker = {
                            size: sdkDataPoint.marker.size?.val,
                            symbol: sdkDataPoint.marker.symbol?.val,
                            props: undefined,
                        };

                        ChartShapeInfoConverter.convertPartialDataFromSdkToUi(
                            sdkDataPoint.marker,
                            uiDataItem.dataPoint.marker as IShapeModel,
                        );
                    }
                }

                if (sdkCategoryItem.dataLabel !== undefined) {
                    uiDataItem.dataLabel = new DataLabel(sdkCategoryItem.dataLabel, this.seriesId, id);
                }

                return {
                    ...uiDataItem,
                    categoryPoint: {
                        value: categoryPoint.v,
                        formatCode: categoryPoint.formatCode,
                    },
                    valuePoint: {
                        value: valuePoint.v,
                        formatCode: valuePoint.formatCode,
                    },
                };
            });
    }

    public setSeriesId() {
        this.seriesId = `ser_${this.idx}_${this.order}_${this.name}`;
    }

    protected setMarker(series: ISubChartSerData) {
        const { marker } = series as WithMarkerSubChartSerData;

        if (marker) {
            this.marker = {
                size: marker.size?.val,
                symbol: marker.symbol?.val,
                props: undefined,
            };

            ChartShapeInfoConverter.convertPartialDataFromSdkToUi(marker, this.marker as unknown as IShapeModel);
        }
    }

    protected setDataLabels(series: ISubChartSerData) {
        if (series.dLbls) {
            const { dLbls } = series;
            if (!dLbls) return dLbls;
            this.dataLabels = new DataLabels(dLbls, this.seriesId);
        }
    }

    protected setTrendline(series: WithTrendlineSubChartSerData) {
        if (!series.trendline) return;
        if (Array.isArray(series.trendline)) {
            this.trendlines = series.trendline.map((trendline, idx) => new Trendline(trendline, this.seriesId, idx));
        } else {
            this.trendlines = [new Trendline(series.trendline, this.seriesId, 0)];
        }
    }
}
